import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import Table from 'shared/components/Table'
import { Pagination } from 'antd'
import Popup from 'shared/components/Popup'
import { Button, Dropdown } from 'semantic-ui-react'
import { images } from 'packages/simplesat-config'
import { useDynamicConstants } from 'config/dynamicConstants'
import useAxios, { mutate } from 'hooks/useAxios'
import useModal from 'hooks/useModal'
import { getAxios } from 'api'
import { useHistory } from 'react-router-dom'

import constants from 'config/constants'

import { fullDateTimeFormat, relativeDateTimeFormat } from 'libs/date-format'
import styled from 'styled-components'
import { Loader } from 'shared/components/Loader'
import { toast } from 'libs/toast'
import { DeleteSurveyModal } from './DeleteSurveyModal'

import useSaveUserActivityLog, { SURVEY_STATUS_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import useWorkspace from 'hooks/useWorkspace'
import { AllowPermission } from 'shared/components/AllowPermission'
import { useNavbarMenuContext } from 'organisms/NavBar/context'

const ACTIVE = 'active'
const CLOSED = 'closed'
const ARCHIVED = 'archived'

const emailSurveyTypesImgMap = {
  ongoing_email: images.emailTypeBoxImg,
  oneoff_email: images.emailTypeBoxImg,
}

/**
 *
 * @param {object} props
 * @param {any[]} props.activeSurveys
 * @param {boolean} props.loading
 * @param {number} props.totalSurvey
 */
const SurveyItems = ({ activeSurveys, loading, totalSurvey }) => {
  const [surveyStatus, setSurveyStatus] = useState(ACTIVE)
  const dynamicConstants = useDynamicConstants()

  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 })
  const [dataSource, setDataSource] = useState([])
  const [total, setTotal] = useState(0)
  const [surveyId, setSurveyId] = useState('')
  const [loadingSurvey, setLoadingSurvey] = useState(false)
  const history = useHistory()
  const workspaceToken = useWorkspace()
  const { updateContextData } = useNavbarMenuContext()

  const saveUserActivityLog = useSaveUserActivityLog()

  const {
    Modal: ModalDelete,
    open: openDelete,
    close: closeDelete,
    isOpen: isOpenDelete,
  } = useModal()

  const { fetch: changeSurveyStatus, loading: loadingStatus } = useAxios({
    method: 'patch',
    skip: true,
  })

  function getNewSurveyData(page, pageSize, type) {
    const params = new URLSearchParams({
      limit: String(pageSize),
      offset: String((page - 1) * pageSize),
      order_by: 'desc',
      status: type,
    })

    if (type === ACTIVE) {
      params.append('exclude_closed', 'true')
    }

    const url = `survey/views/?${params.toString()}`

    setLoadingSurvey(true)

    getAxios()
      .get(url)
      .then((res) => {
        if (res.data) {
          setDataSource(res.data.results)
          setTotal(res.data.count)
          setLoadingSurvey(false)
        }
      })
      .catch((err) => {
        console.error(err)
        toast.error('Oops! Something went wrong. Please try again.')
        setLoadingSurvey(false)
      })
  }

  function handleChangeStatus(id, status) {
    changeSurveyStatus({
      url: `account/survey/${id}`,
      data: { status },
    })
      .then((res) => {
        if (res.status == 200) {
          const newDataSource = dataSource.filter((item) => item.survey.id != id)
          setDataSource(newDataSource)
          const changedSurveyView = dataSource.find((item) => item.survey.id == id)
          if ([ARCHIVED, 'deleted'].includes(status) && changedSurveyView) {
            updateContextData('favorite', { token: changedSurveyView.token, flagged: false })
          }
          mutate('dashboard/list-favorite-view/?type=dashboard&type=survey')
          updateContextData()

          if ([ARCHIVED, 'deleted'].includes(status)) {
            toast.success(`Your survey has been ${status}.`)
          } else {
            toast.success(`Your survey has been restored.`)
          }
          saveUserActivityLog({
            action: SURVEY_STATUS_ACTION_NAME[status],
            target: `sur_${id}`,
            requestBody: { status },
            statusCode: res.status,
          })
          closeDelete()
        }
      })
      .catch((err) => {
        toast.error('Oops! Something went wrong. Please try again.')
        saveUserActivityLog({
          action: SURVEY_STATUS_ACTION_NAME[status],
          target: `sur_${id}`,
          requestBody: { status },
          statusCode: err.response ? err.response.status : 0,
        })
        console.error(err)
      })
  }

  function handlePageChange(page, pageSize) {
    setPagination({ ...pagination, current: page, pageSize })
    getNewSurveyData(page, pageSize, surveyStatus)
  }

  function handleSwitchSurveyType(type) {
    setSurveyStatus(type)
    getNewSurveyData(1, 50, type)
    setPagination({ ...pagination, current: 1, pageSize: 50 })
  }

  function handleOpenDeleteModal(id) {
    setSurveyId(id)
    openDelete()
  }

  function handleRedirectSettingPage(id) {
    history.push(`/${workspaceToken}/settings/surveys/${id}/settings`)
  }

  function handleRedirectDashboardPage(token) {
    history.push(`/${workspaceToken}/dashboards/views/${token}/overview/`)
  }

  //useeffect for recieve first response and save value in state
  useEffect(() => {
    if (activeSurveys) {
      setDataSource(activeSurveys)
      setTotal(totalSurvey)
    }
  }, [setDataSource, activeSurveys, setTotal, totalSurvey])

  return (
    <div className="ui">
      <div className="flex items-center justify-between pb-4">
        <h3 className="m-0">Your surveys</h3>

        <Button.Group basic size="small">
          <Button
            active={surveyStatus === ACTIVE}
            disabled={loadingSurvey}
            onClick={() => handleSwitchSurveyType(ACTIVE)}
          >
            Active
          </Button>
          <Button
            active={surveyStatus === CLOSED}
            disabled={loadingSurvey}
            onClick={() => handleSwitchSurveyType(CLOSED)}
          >
            Closed
          </Button>
          <Button
            active={surveyStatus === ARCHIVED}
            disabled={loadingSurvey}
            onClick={() => handleSwitchSurveyType(ARCHIVED)}
          >
            Archived
          </Button>
        </Button.Group>
      </div>
      {loading && <Loader withContainer />}

      {!loading && dataSource && (
        <SurveyTable
          dataSource={dataSource}
          surveyStatus={surveyStatus}
          handlePageChange={handlePageChange}
          pagination={pagination}
          total={total}
          handleChangeStatus={handleChangeStatus}
          dynamicConstants={dynamicConstants}
          loading={loadingSurvey}
          handleOpenDeleteModal={handleOpenDeleteModal}
          handleRedirectSettingPage={handleRedirectSettingPage}
          handleRedirectDashboardPage={handleRedirectDashboardPage}
          workspaceToken={workspaceToken}
        />
      )}

      {isOpenDelete && (
        <DeleteSurveyModal
          onSubmit={() => handleChangeStatus(surveyId, 'deleted')}
          loading={loadingStatus}
          modal={ModalDelete}
          close={closeDelete}
        />
      )}
    </div>
  )
}

function renderTimestampField(timestamp) {
  if (!timestamp || timestamp === null) {
    return '-'
  }
  return (
    <Popup basic tooltip={fullDateTimeFormat(timestamp)} size="mini" inverted>
      <span>{relativeDateTimeFormat(timestamp)}</span>
    </Popup>
  )
}

function formatTextToUpperCaseFirstLetter(str) {
  const words = str.split('_')

  for (let i = 0; i < words.length; i++) {
    words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1)
  }

  const formattedString = words.join(' ')

  return formattedString
}

const SurveyIcon = ({ img, name }) => {
  return (
    <div className="flex items-center">
      <img src={img} alt={name} height={20} width={20} />
      <span className="ml-2">{name}</span>
    </div>
  )
}

function SurveyTypeIcon(surveyType, dynamicConstants) {
  if (surveyType == 'n/a' || surveyType == '' || surveyType == null) {
    return <span className="ml-2"> - </span>
  } else if (surveyType == 'oneoff_email') {
    return <SurveyIcon img={constants.oneOffEmailIcon} name={'Oneoff email'} />
  } else if (surveyType == 'ongoing_email') {
    return <SurveyIcon img={constants.ongoingEmailIcon} name={'Ongoing email'} />
  } else if (surveyType == 'event_based_email') {
    return <SurveyIcon img={constants.eventBasedEmailIcon} name={'Event-based email'} />
  } else if (surveyType == 'web_embed') {
    return <SurveyIcon img={constants.webEmbedIcon} name={'Web-embed'} />
  }

  const embedSurveyLogoMap = dynamicConstants.surveyConfigurations.reduce(
    (surveyImgMap, helpdesk) => {
      return { ...surveyImgMap, [helpdesk.name]: helpdesk.iconUrl }
    },
    {}
  )
  const surveyImgMap = { ...emailSurveyTypesImgMap, ...embedSurveyLogoMap }
  const image = surveyImgMap[surveyType]
  if (image) {
    return <SurveyIcon img={image} name={formatTextToUpperCaseFirstLetter(surveyType)} />
  }
}

const ActiveSurveyButton = ({
  id,
  viewToken,
  handleChangeStatus,
  handleOpenDeleteModal,
  handleRedirectSettingPage,
  handleRedirectDashboardPage,
}) => {
  return (
    <Dropdown
      icon="far fa-ellipsis-vertical"
      floating
      pointing="top right"
      className="table-elipsis-toolbar-dropdown"
    >
      <Dropdown.Menu>
        <AllowPermission permission={constants.permissions.manageSurveys}>
          <Dropdown.Item
            text="Edit"
            icon="far fa-edit"
            onClick={() => handleRedirectSettingPage(id)}
          />
        </AllowPermission>
        <AllowPermission permission={constants.permissions.manageSurveys}>
          <Dropdown.Item
            text="View survey dashboard"
            icon="far fa-grid-2"
            onClick={() => handleRedirectDashboardPage(viewToken)}
          />
        </AllowPermission>
        <AllowPermission permission={constants.permissions.manageSurveys}>
          <Dropdown.Item
            text="Archive"
            icon="far fa-archive"
            onClick={() => handleChangeStatus(id, ARCHIVED)}
          />
          <Dropdown.Item
            className="!text-negative"
            text="Delete"
            icon="far fa-trash-can"
            onClick={() => handleOpenDeleteModal(id)}
          />
        </AllowPermission>
      </Dropdown.Menu>
    </Dropdown>
  )
}

const ArchivedSurveyButton = ({ id, handleChangeStatus, handleOpenDeleteModal }) => {
  return (
    <AllowPermission permission={constants.permissions.manageSurveys}>
      <Dropdown
        icon="far fa-ellipsis-vertical"
        floating
        pointing="top right"
        className="table-elipsis-toolbar-dropdown"
      >
        <Dropdown.Menu>
          <Dropdown.Item
            text="Restore"
            icon="far fa-trash-restore"
            onClick={() => handleChangeStatus(id, ACTIVE)}
          />
          <Dropdown.Item
            className="!text-negative"
            text="Delete"
            icon="far fa-trash-can"
            onClick={() => handleOpenDeleteModal(id)}
          />
        </Dropdown.Menu>
      </Dropdown>
    </AllowPermission>
  )
}

function SurveyTable({
  dataSource,
  surveyStatus,
  pagination,
  handlePageChange,
  total,
  handleChangeStatus,
  dynamicConstants,
  loading,
  handleOpenDeleteModal,
  handleRedirectSettingPage,
  handleRedirectDashboardPage,
  workspaceToken,
}) {
  const isSurveyActive = surveyStatus === ACTIVE || surveyStatus === CLOSED
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 120,
      render(_, data) {
        return (
          <div className="flex items-center">
            <Popup basic tooltip={<div>{data.survey.name}</div>} size="mini" inverted>
              {isSurveyActive ? (
                <StyledLink
                  data-testid="survey-row-link"
                  className="truncate item"
                  to={`/${workspaceToken}/settings/surveys/${data.survey.id}/settings`}
                >
                  {data.survey.name}
                </StyledLink>
              ) : (
                <div className="truncate">{data.survey.name}</div>
              )}
            </Popup>
          </div>
        )
      },
      sorter(dataA, dataB) {
        const labelA = dataA.survey.name
        const labelB = dataB.survey.name
        return (labelA || '').localeCompare(labelB)
      },
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Delivery method',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render(_, data) {
        return (
          <div className="flex items-center">
            {SurveyTypeIcon(data.survey.type, dynamicConstants)}
          </div>
        )
      },
      sorter(dataA, dataB) {
        const labelA = dataA.survey.type
        const labelB = dataB.survey.type
        return (labelA || '').localeCompare(labelB)
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      width: 80,
      render(_, data) {
        return <div className="leading-normal truncate">{data.survey.brand}</div>
      },
      sorter(dataA, dataB) {
        const labelA = dataA.survey.brand
        const labelB = dataB.survey.brand
        return (labelA || '').localeCompare(labelB)
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Created',
      key: 'created',
      dataIndex: 'date',
      align: 'left',
      width: 70,
      render(_, data) {
        return renderTimestampField(data.survey.created)
      },
      // @ts-ignore
      sorter: (a, b) => new Date(a.survey.created) - new Date(b.survey.created),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Modified',
      key: 'modified',
      align: 'left',
      width: 70,
      render(_, data) {
        return renderTimestampField(data.survey.modified)
      },
      // @ts-ignore
      sorter: (a, b) => new Date(a.survey.modified) - new Date(b.survey.modified),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      width: 30,
      render(_, data) {
        if (isSurveyActive) {
          return (
            <ActiveSurveyButton
              id={data.survey.id}
              viewToken={data.token}
              handleChangeStatus={handleChangeStatus}
              handleOpenDeleteModal={handleOpenDeleteModal}
              handleRedirectSettingPage={handleRedirectSettingPage}
              handleRedirectDashboardPage={handleRedirectDashboardPage}
            />
          )
        } else {
          return (
            <ArchivedSurveyButton
              id={data.survey.id}
              handleChangeStatus={handleChangeStatus}
              handleOpenDeleteModal={handleOpenDeleteModal}
            />
          )
        }
      },
    },
  ]

  return (
    <div>
      <Table
        variant="indexPage"
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        loading={loading}
      />
      {total >= 50 && (
        <div className="flex justify-end mt-4">
          <Pagination
            total={total}
            current={pagination.current}
            pageSize={pagination.pageSize}
            pageSizeOptions={['50', '100', '500']}
            showSizeChanger
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
          />
        </div>
      )}
    </div>
  )
}

const StyledLink = styled(Link)`
  color: #373f46;
  &:hover {
    color: var(--ss-link-color);
  }
`

SurveyItems.propTypes = {
  activeSurveys: PropTypes.array,
  loading: PropTypes.bool,
  totalSurvey: PropTypes.number,
}

export default SurveyItems



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/SurveyItems/index.js